import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './lib/auth.jsx';
import Layout from './components/Layout.jsx';
import Login from './pages/Login.jsx';
import ChangePassword from './pages/ChangePassword.jsx';
import Dashboard from './pages/Dashboard.jsx';
import PatientList from './pages/PatientList.jsx';
import PatientDetail from './pages/PatientDetail.jsx';
import Deliveries from './pages/Deliveries.jsx';
import Alerts from './pages/Alerts.jsx';
import KPIs from './pages/KPIs.jsx';
import Admin from './pages/Admin.jsx';
import AdminUsers from './pages/AdminUsers.jsx';
import AdminLookup from './pages/AdminLookup.jsx';
import AdminBranding from './pages/AdminBranding.jsx';
import AdminPayments from './pages/AdminPayments.jsx';
import AdminPaymentsAudit from './pages/AdminPaymentsAudit.jsx';
import Payment from './pages/Payment.jsx';
import VideoCall from './pages/VideoCall.jsx';
import Landing from './pages/Landing.jsx';
import LegalTerms from './pages/LegalTerms.jsx';
import LegalPrivacy from './pages/LegalPrivacy.jsx';
import HelpFaq from './pages/HelpFaq.jsx';
import PlatformLogin        from './pages/PlatformLogin.jsx';
import PlatformLayout       from './pages/PlatformLayout.jsx';
import PlatformTenants      from './pages/PlatformTenants.jsx';
import PlatformTenantDetail from './pages/PlatformTenantDetail.jsx';
import PlatformAudit        from './pages/PlatformAudit.jsx';
import PlatformHealth       from './pages/PlatformHealth.jsx';
import { PlatformAuthProvider } from './lib/platformAuth.jsx';
import Shipments from './pages/Shipments.jsx';
import ShipmentNew from './pages/ShipmentNew.jsx';
import ShipmentDetail from './pages/ShipmentDetail.jsx';
import Watchlist from './pages/Watchlist.jsx';
import { TenantRoot, NoTenantPath } from './lib/tenantPath.jsx';

function Protected({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password && window.location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }
  return children;
}

// Anonymous users on "/" see the marketing landing page; signed-in users get
// the dashboard shell. Anonymous users hitting any other protected path are
// pushed to /login so the route Outlet can do its thing once they auth.
function Root() {
  const { user } = useAuth();
  if (user) {
    if (user.must_change_password && window.location.pathname !== '/change-password') {
      return <Navigate to="/change-password" replace />;
    }
    return <NoTenantPath><Layout /></NoTenantPath>;
  }
  if (window.location.pathname === '/') return <Landing />;
  return <Navigate to="/login" replace />;
}

// Phase 8.4 — App page tree.  Used twice: once unprefixed (legacy), once
// under /t/:slug/* (tenant-scoped URLs).  Defining the children once keeps
// the trees in sync.
function appChildRoutes() {
  return [
    <Route key="idx"          index                                element={<Dashboard />} />,
    <Route key="patients"     path="patients"                      element={<PatientList />} />,
    <Route key="patient"      path="patients/:id"                  element={<PatientDetail />} />,
    <Route key="deliveries"   path="deliveries"                    element={<Deliveries />} />,
    <Route key="alerts"       path="alerts"                        element={<Alerts />} />,
    <Route key="kpis"         path="kpis"                          element={<KPIs />} />,
    <Route key="admin"        path="admin"                         element={<Admin />} />,
    <Route key="adm-users"    path="admin/users"                   element={<AdminUsers />} />,
    <Route key="adm-lookup"   path="admin/lookup"                  element={<AdminLookup />} />,
    <Route key="adm-brand"    path="admin/branding"                element={<AdminBranding />} />,
    <Route key="adm-pay"      path="admin/payments"                element={<AdminPayments />} />,
    <Route key="adm-pay-aud"  path="admin/payments-audit"          element={<AdminPaymentsAudit />} />,
    <Route key="ship"         path="shipments"                     element={<Shipments />} />,
    <Route key="ship-new"     path="shipments/new"                 element={<ShipmentNew />} />,
    <Route key="ship-detail"  path="shipments/:id"                 element={<ShipmentDetail />} />,
    <Route key="watchlist"    path="watchlist"                     element={<Watchlist />} />,
    <Route key="pay"          path="payment/:patientId"            element={<Payment />} />,
    <Route key="call"         path="call/:patientId"               element={<VideoCall />} />,
  ];
}

export default function App() {
  return (
    <PlatformAuthProvider>
    <Routes>
      {/* Public routes (no auth) */}
      <Route path="/login"           element={<Login />} />
      {/* Phase 8.4 — tenant-scoped login deep link; same Login component
          reads the slug from useParams() (path) or ?tenant= (query). */}
      <Route path="/t/:slug/login"   element={<Login />} />
      <Route path="/landing"         element={<Landing />} />
      <Route path="/legal/terms"     element={<LegalTerms />} />
      <Route path="/legal/privacy"   element={<LegalPrivacy />} />
      <Route path="/help/faq"        element={<HelpFaq />} />

      {/* Platform / super-admin tree — separate JWT, separate session storage */}
      <Route path="/platform/login" element={<PlatformLogin />} />
      <Route path="/platform" element={<PlatformLayout />}>
        <Route index element={<PlatformTenants />} />
        <Route path="tenants/:id" element={<PlatformTenantDetail />} />
        <Route path="audit" element={<PlatformAudit />} />
        <Route path="health" element={<PlatformHealth />} />
      </Route>

      <Route path="/change-password" element={<Protected><ChangePassword /></Protected>} />

      {/* Phase 8.4 — Tenant-scoped tree.  /t/:slug/* mirrors the unprefixed
          tree below.  Backend tenant isolation comes from the JWT, not the
          URL — the slug is for bookmarks, breadcrumbs, and impersonation UX. */}
      <Route path="/t/:slug" element={<TenantRoot />}>
        {appChildRoutes()}
      </Route>

      {/* Authenticated app shell — anonymous users get the Landing page instead.
          Legacy unprefixed paths kept for backwards compatibility. */}
      <Route path="/" element={<Root />}>
        {appChildRoutes()}
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </PlatformAuthProvider>
  );
}
